import { useEffect, useState } from 'react';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { displayToastSuccess } from '@/helpers/display-toast';
import { usePermissions } from '@/modules/common';
import { FeatureMode } from '@/modules/common/components/templates/feature-details/types/types';
import { useApiError } from '@/modules/common/hooks/use-api-error';
import { useFeatureTabNavigation } from '@/modules/common/hooks/use-feature-tab-navigation';
import { OpsiumPolicy } from '@/modules/common/types/opsium-policy';
import { invalidateClientQueries } from '@/modules/common/utils/query-invalidation';

import { createClient, getClientDetails, updateClient, uploadClientLogo } from '../api/clients.api';
import { ClientIntegration, ClientTab } from '../types';
import { CreateClientRequest } from '../types/dto/request/create-client.dto';
import { UpdateClientRequest } from '../types/dto/request/update-client.dto';
import { usePatchClientIntegrations } from './use-patch-client-integrations';

interface ClientSaveParams {
  data: (CreateClientRequest & { avatar?: File }) | UpdateClientRequest;
  integrations?: ClientIntegration[];
  successCallback?: () => void;
}

interface LogoUploadParams {
  clientId: string;
  file: File;
}

/**
 * Manages client details state and operations for both create and edit modes.
 * Handles client CRUD operations, tab navigation, and file upload functionality.
 */
export function useClientDetails() {
  const { clientId } = useParams<{ clientId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { t } = useTranslation(['authenticated.clients']);
  const { displayToastErrorFromApiWithFallback } = useApiError();

  const { hasPermission } = usePermissions();
  const isEditClientsEnabled = hasPermission(OpsiumPolicy.ClientsEdit);

  const defaultMode =
    clientId === 'new'
      ? FeatureMode.Create
      : isEditClientsEnabled
        ? FeatureMode.Edit
        : FeatureMode.Read;

  const [mode, setMode] = useState<FeatureMode>(defaultMode);
  const [onSaveSuccess, setOnSaveSuccess] = useState<(() => void) | null>(null);

  // Update mode when permissions load or change (handles page refresh scenario)
  useEffect(() => {
    // Only update if we're not in create mode
    if (clientId !== 'new') {
      const newMode = isEditClientsEnabled ? FeatureMode.Edit : FeatureMode.Read;
      setMode(newMode);
    }
  }, [clientId, isEditClientsEnabled]);

  // Use hybrid tab navigation hook
  const { currentTab, setCurrentTab, navigateToTab } = useFeatureTabNavigation({
    mode,
    defaultTab: ClientTab.Info,
    validTabs: Object.values(ClientTab),
    entityId: clientId,
  });

  const {
    data: clientData,
    isLoading: isLoadingClient,
    error: clientError,
  } = useQuery({
    queryKey: ['clients', clientId],
    queryFn: () => getClientDetails(clientId),
    enabled: !!clientId && clientId !== 'new',
  });

  const createClientMutation = useMutation({
    mutationFn: async (data: CreateClientRequest & { avatar?: File }) => {
      // Step 1: Create the client (returns only ID)
      const response = await createClient(data);
      const clientId = response.id;

      // Step 2: Upload avatar if provided
      if (data.avatar instanceof File) {
        await uploadClientLogo({ clientId: clientId.toString(), file: data.avatar });
      }

      return clientId;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      displayToastSuccess(
        t('authenticated.clients:toasts.clientCreatedSuccess', { name: variables.info.name })
      );

      // Call success callback before navigation (allows navigation guard to be bypassed)
      if (onSaveSuccess) {
        onSaveSuccess();
        setOnSaveSuccess(null);
      }

      navigate(`/clients`, { replace: true });
    },
    onError: error => {
      displayToastErrorFromApiWithFallback(error, 'authenticated.clients:toasts.createClientError');
    },
  });

  const updateClientMutation = useMutation({
    mutationFn: updateClient,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      displayToastSuccess(t('authenticated.clients:toasts.clientUpdatedSuccess'));

      if (onSaveSuccess) {
        onSaveSuccess();
        setOnSaveSuccess(null);
      }
    },
    onError: error => {
      displayToastErrorFromApiWithFallback(error, 'authenticated.clients:toasts.updateClientError');
    },
  });

  const patchClientIntegrationsMutation = usePatchClientIntegrations();

  const uploadLogoMutation = useMutation({
    mutationFn: ({ clientId, file }: LogoUploadParams) => uploadClientLogo({ clientId, file }),
    onSuccess: async () => {
      await invalidateClientQueries(queryClient);
      if (clientId && clientId !== 'new') {
        queryClient.invalidateQueries({ queryKey: ['clients', clientId] });
      }
      displayToastSuccess(t('authenticated.clients:toasts.logoUploadedSuccess'));
    },
    onError: error => {
      displayToastErrorFromApiWithFallback(error, 'authenticated.clients:toasts.logoUploadError');
    },
  });

  const handleSaveClient = ({ data, integrations, successCallback }: ClientSaveParams) => {
    if (successCallback) {
      setOnSaveSuccess(() => successCallback);
    }

    if (mode === FeatureMode.Create) {
      createClientMutation.mutate(data as CreateClientRequest & { avatar?: File });
      return;
    }

    if (currentTab === ClientTab.Integrations) {
      if (!clientId) return;

      patchClientIntegrationsMutation.mutate({
        clientId,
        integrations,
      });
      return;
    }

    updateClientMutation.mutate({
      ...data,
      id: Number(clientId),
    } as UpdateClientRequest);
  };

  const handleTabChange = (tab: ClientTab | string, additionalParams?: Record<string, string>) => {
    const targetTab = typeof tab === 'string' ? (tab as ClientTab) : tab;

    if (additionalParams) {
      // Use navigateToTab when additional params are provided
      navigateToTab(targetTab, additionalParams);
    } else {
      // Simple tab change without additional params
      setCurrentTab(targetTab);
    }
  };

  const handleLogoUpload = (file: File) => {
    if (clientId && clientId !== 'new') {
      uploadLogoMutation.mutate({ clientId, file });
    }
  };

  const isLoading =
    createClientMutation.isPending ||
    updateClientMutation.isPending ||
    patchClientIntegrationsMutation.isPending;

  return {
    // Data
    clientData,
    mode,
    currentTab,

    // Loading states
    isLoading,
    isLoadingClient,
    isUploadingLogo: uploadLogoMutation.isPending,

    // Error states
    error: clientError,

    // Actions
    handleSaveClient,
    handleTabChange,
    handleLogoUpload,
    navigateToTab,
  };
}
